import React, { useEffect, useRef, useState } from "react";
import { SelectionContainer } from "../";
import { Radio } from "@mui/material";
import { useApi } from "../../../../../common/hooks";
import { LinkReqToDelivService } from "../../../../../api";
import { useDispatch, useSelector } from "react-redux";
import styles from "../EnvironmentSelection.module.css";
import { translate } from "../../../../../common/providers";
import { CustomButtonLink, CustomTooltip, IconComponent, icon } from "../../../../../common/components";
import { setDeliverable, setDeliverableToUpdate, setProjectReqToDelivToUpdate } from "../../../../../common/slice";
import { setUpdateReqCounters, setUpdateReqList } from "../../../slice/link-requirement-to-deliverable-slice";

const DeliverableSelection = () => {
  const { call: getDeliverables } = useApi(LinkReqToDelivService.getDeliverables);
  const projectReqToDelivId = useSelector(({ context }) => context.projectReqToDeliv.id);
  const domain = useSelector(({ context }) => context.domain);
  const companyId = useSelector(({ context }) => context.company?.id);
  const deliverable = useSelector(({ context }) => context.deliverable);
  const deliverableRef = useRef(deliverable);
  const [request, setRequest] = useState(null);
  const [selectedDeliverable, setSelectedDeliverable] = useState(deliverable);
  const dispatch = useDispatch();
  useEffect(() => {
    deliverableRef.current = deliverable;
  }, [deliverable]);
  useEffect(() => {
    setRequest(
      () =>
        ({ page, limit, search }) =>
          getDeliverables({ projectId: projectReqToDelivId }, { page, limit, filter: search }).then((data) => {
            // Update current deliverable's lock status if it's in the list
            const currentDeliverable = deliverableRef.current;
            if (currentDeliverable?.id && data?.contents) {
              const currentDeliverableInList = data.contents.find((d) => d.id === currentDeliverable.id);
              if (
                currentDeliverableInList &&
                currentDeliverableInList.phaseLockedOrArchived !== currentDeliverable.phaseLockedOrArchived
              ) {
                dispatch(
                  setDeliverable({
                    ...currentDeliverable,
                    phaseLockedOrArchived: currentDeliverableInList.phaseLockedOrArchived,
                  })
                );
              }
            }
            return data;
          })
    );
  }, [projectReqToDelivId, getDeliverables, dispatch]);
  const handleSelectProject = (row) => {
    setSelectedDeliverable(row);
  };
  const rowRenderer = (row) => {
    const isLocked = row.phaseLockedOrArchived === true;
    const isSelected = row.id === selectedDeliverable?.id;
    return (
      <div
        className={`${styles.rowContainer} ${isLocked ? styles.rowContainerLocked : ""}`}
        role="presentation"
        onClick={() => handleSelectProject(row)}
      >
        <Radio radio checked={isSelected} />
        <CustomTooltip showWhenOverflow title={row.name}>
          <div className={`${styles.name} ${isLocked ? styles.nameLocked : ""}`}>
            {row.name}
            {isLocked && (
              <IconComponent className={styles.lockIcon} color="var(--color-grey)" icon={icon.faLock} size="sm" />
            )}
          </div>
        </CustomTooltip>
        <CustomTooltip showWhenOverflow title={row.owner?.displayName}>
          <div className={`${styles.name} ${isLocked ? styles.nameLocked : ""}`}>{row.owner?.displayName}</div>
        </CustomTooltip>
      </div>
    );
  };

  const headerRow = () => (
    <div className={styles.headerRowContainer}>
      <div className={styles.radio} />
      <div className={styles.name}>{translate("common:project-selection-side-pane.name")}</div>
      <div className={styles.name}>{translate("common:project-selection-side-pane.owner")}</div>
    </div>
  );
  const handleValidate = () => {
    if (!selectedDeliverable?.id) {
      return;
    }
    dispatch(
      setDeliverable({
        name: selectedDeliverable.name,
        id: selectedDeliverable.id,
        ownerId: selectedDeliverable.owner?.id,
        ownerName: selectedDeliverable.owner?.displayName,
        phaseLockedOrArchived: selectedDeliverable.phaseLockedOrArchived,
      })
    );
    dispatch(setDeliverableToUpdate(false));
    dispatch(setUpdateReqList(true));
    dispatch(setUpdateReqCounters(true));
  };
  const handleBack = () => {
    dispatch(setProjectReqToDelivToUpdate(true));
  };
  const noRowsRenderer = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyState__header}>{translate("common:empty-state.no-results")}</div>
      <div className={styles.emptyState__content}>
        <span>{translate("requirements-to-deliverables.deliverable-selection.to-cover")}</span>
        <CustomButtonLink
          external
          openInNewTab
          buttonClassName={styles.emptyState__link}
          to={`https://${domain}/projects/${projectReqToDelivId}/deliverables/list?companyId=${companyId}`}
        >
          {translate("requirements-to-deliverables.deliverable-selection.delivcerables-to-be-produced")}
        </CustomButtonLink>
        <span className={styles.emptyState__lowerCase}>
          {translate("requirements-to-deliverables.deliverable-selection.to-cover.pages")}
        </span>
      </div>
    </div>
  );
  return (
    <SelectionContainer
      disableValidate={!selectedDeliverable?.id}
      headerRow={headerRow}
      noRowsRenderer={noRowsRenderer}
      request={request}
      rowRenderer={rowRenderer}
      title="Sélectionner un livrable"
      onBack={handleBack}
      onValidate={handleValidate}
    />
  );
};

export default DeliverableSelection;
